import { cva } from "class-variance-authority";
import type { FactoryOpts, InputMask } from "imask";
import { XIcon } from "lucide-react";
import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useIMask } from "react-imask";
import { useTranslation } from "../../i18n";
import { cn, composeEventHandlers, mergeRefs } from "../../utils";
import { IconButton } from "../Button/IconButton";
import { type ButtonSize } from "../Button/types";
import { Spinner } from "../Spinner";
import { type SpinnerColor, type SpinnerSize } from "../Spinner/types";
import type { InputProps, InputSize } from "./types";

const inputContainerVariants = cva(
  "group/input box-border w-full relative flex items-center border overflow-hidden border-neutral-100 hover:border-neutral-200 hover:outline hover:outline-[2px] hover:outline-neutral-200 has-[:focus:not(button)]:outline has-[:focus:not(button)]:outline-[2px] has-[:focus:not(button)]:outline-primary-500 has-[:focus:not(button)]:hover:outline-primary-500",
  {
    variants: {
      size: {
        sm: "h-8 rounded-xl text-sm",
        md: "h-10 rounded-xl text-sm",
        lg: "h-12 rounded-xl text-md",
      },
      invalid: {
        true: "outline outline-[2px] border-danger-500 outline-danger-500 hover:border-danger-500 hover:outline-danger-500 has-[:focus:not(button)]:border-danger-500 has-[:focus:not(button)]:outline-danger-500 has-[:focus:not(button)]:hover:border-danger-500 has-[:focus:not(button)]:hover:outline-danger-500",
      },
      disabled: {
        true: "pointer-events-none text-neutral-200 bg-neutral-100 border-neutral-200",
        false: "bg-white",
      },
      hasStartAccessory: {
        true: "ps-[3px]",
      },
      hasEndAccessory: {
        true: "pe-[3px]",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const inputFieldVariants = cva(
  "flex w-full h-full outline-none bg-transparent border-0 cursor-text text-neutral-900 placeholder:text-neutral-500/50 focus:outline-none",
  {
    variants: {
      disabled: {
        true: "text-neutral-500 cursor-not-allowed",
      },
      hasStartAccessory: {
        false: "ps-4",
      },
      hasEndAccessory: {
        false: "pe-4",
      },
    },
  },
);

const accessoryVariants = cva("flex items-center justify-center", {
  variants: {
    size: {
      sm: "h-6",
      md: "h-8",
      lg: "h-10",
    },
  },
});

const spinnerContainerVariants = cva("flex items-center justify-center", {
  variants: {
    size: {
      sm: "w-6 h-6",
      md: "w-8 h-8",
      lg: "w-10 h-10",
    },
  },
});

export const Input = ({
  ref,
  size: sizeProp = "md",
  startAccessory,
  endAccessory,
  loading,
  invalid,
  spinnerProps,
  onClear,
  onChange,
  regex,
  mask,
  inputClassName,
  className,
  disabled,
  value: valueProp,
  onKeyDown,
  ...inputProps
}: InputProps) => {
  const { t } = useTranslation();
  const size: InputSize = sizeProp;
  const internalRef = useRef<HTMLInputElement>(null);

  const hasStartAccessory = !!startAccessory;
  const hasEndAccessory = !!(endAccessory || onClear || loading);

  const rootClass = cn(
    inputContainerVariants({
      size,
      invalid,
      disabled: !!disabled,
      hasStartAccessory,
      hasEndAccessory,
    }),
    className,
  );

  const inputClass = cn(
    inputFieldVariants({
      disabled,
      hasStartAccessory,
      hasEndAccessory,
    }),
    inputClassName,
  );

  const maskOptions = useMemo(() => {
    if (!mask) return null;

    if (typeof mask === "string") {
      return { mask };
    }

    return mask;
  }, [mask]);

  const handleMaskAccept = useCallback(
    (value: string, maskInstance: InputMask<FactoryOpts>) => {
      if (!maskOptions) return;

      let outputValue: string = value;
      if (maskOptions.unmask === true) {
        outputValue = maskInstance.unmaskedValue;
      } else if (maskOptions.unmask === "typed") {
        outputValue = maskInstance.typedValue;
      }

      onChange?.(outputValue, undefined);
      maskOptions.onAccept?.(value, maskInstance);
    },
    [maskOptions, onChange],
  );

  const handleMaskComplete = useCallback(
    (value: string, maskInstance: InputMask<FactoryOpts>) => {
      maskOptions?.onComplete?.(value, maskInstance);
    },
    [maskOptions],
  );

  const {
    ref: maskRef,
    maskRef: imaskInstanceRef,
    value: maskValue,
    setValue: setMaskValue,
    setUnmaskedValue,
  } = useIMask<HTMLInputElement>(
    maskOptions ? maskOptions : { mask: "" },
    maskOptions
      ? {
          onAccept: handleMaskAccept,
          onComplete: handleMaskComplete,
          ref,
        }
      : undefined,
  );

  const inputRef = mergeRefs(internalRef, maskRef, ref);

  useEffect(() => {
    if (!maskOptions) return;
    if (internalRef.current && document.activeElement === internalRef.current)
      return;

    const newValue = valueProp ?? "";
    if (newValue !== maskValue) {
      setMaskValue(String(newValue));

      if (newValue === "") {
        setUnmaskedValue("");
        if (imaskInstanceRef.current) {
          imaskInstanceRef.current.value = "";
          imaskInstanceRef.current.updateValue();
        }
      }
    }
  }, [
    valueProp,
    maskOptions,
    maskValue,
    setMaskValue,
    setUnmaskedValue,
    imaskInstanceRef,
  ]);

  const buttonSizeMap: Record<InputSize, ButtonSize> = {
    sm: "xs",
    md: "sm",
    lg: "md",
  };

  const spinnerSizeMap: Record<InputSize, SpinnerSize> = {
    sm: "2xs",
    md: "xs",
    lg: "xs",
  };

  const spinnerColor: SpinnerColor = spinnerProps?.color || "primary";
  const spinnerSize: SpinnerSize = spinnerProps?.size || spinnerSizeMap[size];

  const accessoryClass = accessoryVariants({ size });
  const spinnerClass = spinnerContainerVariants({ size });

  const handleClear = useCallback(
    (e: MouseEvent) => {
      if (maskOptions) {
        setMaskValue("");
        setUnmaskedValue("");
        if (imaskInstanceRef.current) {
          imaskInstanceRef.current.value = "";
          imaskInstanceRef.current.updateValue();
        }
        onChange?.("", undefined);
      }
      onClear?.(e);
      internalRef.current?.focus();
    },
    [
      maskOptions,
      setMaskValue,
      setUnmaskedValue,
      imaskInstanceRef,
      onChange,
      onClear,
    ],
  );

  const clearButtonJSX = !disabled && onClear && (
    <IconButton
      variant="ghost"
      color="neutral"
      size={buttonSizeMap[size]}
      onClick={handleClear}
      content={<XIcon />}
      aria-label={t("input.clearField")}
    />
  );

  const spinnerJSX = !disabled && loading && (
    <div className={spinnerClass}>
      <Spinner color={spinnerColor} size={spinnerSize} {...spinnerProps} />
    </div>
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      if (maskOptions) return;

      let value = e.currentTarget.value;

      if (regex) {
        value = value
          .split("")
          .filter((char) => regex.test(char))
          .join("");
      }

      if (onChange) {
        onChange(value, e);
      }
    },
    [onChange, regex, maskOptions],
  );

  const value: string = useMemo(() => {
    return maskOptions ? maskValue : valueProp?.toString() || "";
  }, [valueProp, maskOptions, maskValue]);

  const internalKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!regex) return;

      const isValidCharacter = regex.test(e.key);
      const navigationKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Tab",
        "Enter",
      ];

      if (!isValidCharacter && !navigationKeys.includes(e.key)) {
        e.preventDefault();
      }
    },
    [regex],
  );

  const onKeyDownComposed = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => composeEventHandlers(onKeyDown as any, internalKeyDown as any),
    [onKeyDown, internalKeyDown],
  );

  return (
    <div className={rootClass} style={{ outlineOffset: "-1px" }}>
      {startAccessory && (
        <div className="flex items-center text-neutral-500">
          <div className={accessoryClass}>{startAccessory}</div>
        </div>
      )}
      <input
        {...inputProps}
        ref={inputRef}
        disabled={disabled}
        size={undefined}
        className={inputClass}
        onChange={handleChange}
        onKeyDown={onKeyDownComposed}
        value={maskOptions ? undefined : valueProp}
        aria-invalid={invalid}
      />
      {(clearButtonJSX || endAccessory || spinnerJSX) && (
        <div className="flex items-center">
          {value.length > 0 && clearButtonJSX}
          {!spinnerJSX && endAccessory && (
            <div className="flex items-center text-neutral-500">
              <div className={accessoryClass}>{endAccessory}</div>
            </div>
          )}
          {spinnerJSX}
        </div>
      )}
    </div>
  );
};
